import { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { MantineProvider, createTheme, Button, Text, Group, Paper, Loader } from '@mantine/core';
import { IconServer } from '@tabler/icons-react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { setApiBase, fetchStats, setAuthToken, fetchLogs, handshake } from './api';
import type { Stats, LogEntry, Settings } from './types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

const theme = createTheme({
  primaryColor: 'blue',
  defaultRadius: 'md',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontFamilyMonospace: 'JetBrains Mono, monospace',
  components: {
    Button: {
      defaultProps: {
        transitionProps: { duration: 200, transition: 'scale' },
      },
    },
  },
});

// Backend Setup Component
function BackendSetup({ onSetBackend }: { onSetBackend: (url: string) => void }) {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<'idle' | 'testing' | 'connected' | 'error'>('idle');
  const [testing, setTesting] = useState(false);
  const [normalizedUrl, setNormalizedUrl] = useState('');

  const normalizeUrl = (input: string): string => {
    let trimmed = input.trim().toLowerCase();
    trimmed = trimmed.replace(/^https?:\/\//i, '');
    const slashIndex = trimmed.indexOf('/');
    if (slashIndex !== -1) trimmed = trimmed.substring(0, slashIndex);
    trimmed = trimmed.replace(/[^a-z0-9.-]/g, '');
    trimmed = trimmed.replace(/\.+/g, '.');
    trimmed = trimmed.replace(/^\.|\.$/g, '');
    return trimmed;
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setUrl(raw);
    const normalized = normalizeUrl(raw);
    setNormalizedUrl(normalized);
    if (status !== 'idle' && status !== 'error') setStatus('idle');
  };

  const testConnection = async () => {
    if (!normalizedUrl) return;
    setTesting(true);
    setStatus('testing');
    const fullUrl = `https://${normalizedUrl}`;
    try {
      const isValid = await handshake(fullUrl);
      if (isValid) {
        setStatus('connected');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (normalizedUrl.length > 3 && normalizedUrl.includes('.')) testConnection();
    }, 800);
    return () => clearTimeout(delayDebounce);
  }, [normalizedUrl]);

  const handleSubmit = () => {
    if (status === 'connected') {
      onSetBackend(`https://${normalizedUrl}`);
    }
  };

  const statusColors = {
    idle: 'var(--mantine-color-gray-6)',
    testing: 'var(--mantine-color-yellow-6)',
    connected: 'var(--mantine-color-green-6)',
    error: 'var(--mantine-color-red-6)',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Paper shadow="xl" radius="lg" p="xl" w={450} style={{ backdropFilter: 'blur(10px)', background: 'rgba(255,255,255,0.95)' }}>
        <Group justify="center" mb="lg">
          <Paper bg="blue.6" p="md" radius="xl">
            <IconServer size={32} color="white" />
          </Paper>
        </Group>
        <Text size="xl" fw={700} ta="center" mb="xs">Sinker DNS</Text>
        <Text size="sm" c="dimmed" ta="center" mb="xl">Connect to your DNS Gateway</Text>

        <div style={{ marginBottom: '20px' }}>
          <Text size="sm" fw={500} mb="xs">Worker Backend URL</Text>
          <Group gap="xs">
            <input
              type="text"
              value={url}
              onChange={handleUrlChange}
              placeholder="your-worker.workers.dev"
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '8px',
                border: `2px solid ${statusColors[status]}`,
                outline: 'none',
                fontSize: '14px',
              }}
              autoFocus
            />
            <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: statusColors[status], animation: status === 'testing' ? 'pulse 1s infinite' : 'none' }} />
          </Group>
          {status === 'connected' && <Text size="xs" c="green" mt="5">✓ Gateway found</Text>}
          {status === 'error' && <Text size="xs" c="red" mt="5">✗ Cannot connect. Check URL</Text>}
        </div>

        <Button
          fullWidth
          size="lg"
          variant="gradient"
          gradient={{ from: 'blue', to: 'cyan' }}
          onClick={handleSubmit}
          disabled={status !== 'connected'}
          loading={testing}
        >
          {status === 'connected' ? 'Continue to Login →' : 'Checking connection...'}
        </Button>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </Paper>
    </div>
  );
}

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [backendUrl, setBackendUrl] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);

  useEffect(() => {
    const savedUrl = localStorage.getItem('sinker_backend_url');
    const savedToken = localStorage.getItem('sinker_token');
    
    if (savedUrl) {
      setBackendUrl(savedUrl);
      setApiBase(savedUrl);
      
      if (savedToken) {
        setAuthToken(savedToken);
        Promise.all([fetchStats(), fetchLogs(), import('./api').then(m => m.fetchSettings())])
          .then(([statsData, logsData, settingsData]) => {
            setStats(statsData);
            setLogs(logsData.logs);
            setSettings(settingsData);
            setIsAuthenticated(true);
          })
          .catch(() => {
            localStorage.removeItem('sinker_token');
            setAuthToken(null);
          })
          .finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleSetBackend = (url: string) => {
    setBackendUrl(url);
    setApiBase(url);
    localStorage.setItem('sinker_backend_url', url);
    localStorage.removeItem('sinker_token');
    setAuthToken(null);
    setIsAuthenticated(false);
  };

  const handleLogin = async () => {
    const [statsData, logsData, settingsData] = await Promise.all([
      fetchStats(),
      fetchLogs(),
      import('./api').then(m => m.fetchSettings())
    ]);
    setStats(statsData);
    setLogs(logsData.logs);
    setSettings(settingsData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setAuthToken(null);
    setIsAuthenticated(false);
    setStats(null);
    setLogs([]);
    setSettings(null);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader size="xl" />
      </div>
    );
  }

  if (!backendUrl) {
    return <BackendSetup onSetBackend={handleSetBackend} />;
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} backendUrl={backendUrl} onBackToSetup={() => setBackendUrl(null)} />;
  }

  return <Dashboard onLogout={handleLogout} stats={stats} logs={logs} settings={settings} onSettingsChange={setSettings} />;
}

function App() {
  return (
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-right" richColors closeButton />
        <AppContent />
      </QueryClientProvider>
    </MantineProvider>
  );
}

export default App;
